import cx from 'classnames'
import { useContext, useMemo } from 'react'

import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import { FrameBuilderSelectedStateContext } from '@frame-builder/contexts/state-context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'
import { FrameBuilderValidationContext } from '@frame-builder/contexts/validation-context'
import {
  getAllStepDefinitions,
  getFilteredStepDefinitions,
  getIsStepEnabled,
} from '@frame-builder/lib/steps'
import { type FrameBuilderStep } from '@frame-builder/lib/types'

import NewLineToBr from '@frame-builder/components/utility/new-line-to-br'

interface PreviewListProps {
  openStep: FrameBuilderStep | null
  onToggleStep: (step: FrameBuilderStep, isStepOpen: boolean) => void
  className?: string
}

const PreviewList = ({
  openStep,
  onToggleStep,
  className,
}: PreviewListProps) => {
  const { selectedState } = useContext(FrameBuilderSelectedStateContext)
  const { frameBuilderConfiguration, frameBuilderType } = useContext(
    FrameBuilderSettingsContext,
  )
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const { frameCompletionPercentage } = useContext(
    FrameBuilderValidationContext,
  )

  const enabledStepDefinitions = useMemo(() => {
    const allStepDefinitions = getAllStepDefinitions()

    return getFilteredStepDefinitions(allStepDefinitions, (stepDefinition) => {
      return getIsStepEnabled(
        frameBuilderType,
        frameBuilderConfiguration,
        selectedState,
        stepDefinition.step,
      )
    })
  }, [frameBuilderConfiguration, frameBuilderType, selectedState])

  return (
    <div
      className={cx(
        'grow-0 shrink-0 w-full transition-all duration-200',
        {
          '-ml-[100%]': openStep !== null,
        },
        className,
      )}
    >
      {enabledStepDefinitions.map((stepDefinition) => {
        const PreviewComponent = stepDefinition.previewComponent

        return (
          <PreviewComponent
            key={stepDefinition.step}
            stepNumber={stepDefinition.stepNumber}
            openStep={openStep}
            onToggleStep={onToggleStep}
          />
        )
      })}

      <p className="py-6 max-w-48 text-center mx-auto">
        {frameCompletionPercentage < 100 && (
          <NewLineToBr>
            {frameBuilderStrings.controlChooseProductHint}
          </NewLineToBr>
        )}
        {frameCompletionPercentage === 100 && (
          <NewLineToBr>{frameBuilderStrings.controlAddToCartHint}</NewLineToBr>
        )}
      </p>
    </div>
  )
}

export default PreviewList
